import type { Dispatch } from '@reduxjs/toolkit';
import type { GetRootState } from 'js/src/libs/RootState';

import {
    getTranscriptById,
    getTranscriptIds,
    sceneGroupHasTranscriptTranslation,
} from 'js/src/data/transcript/transcript-selectors';
import type { SerializedTranscript } from 'js/src/data/transcript/transcript-types';
import { reduceTranscriptsToTranslationEditorsMap } from 'js/src/data/translated-editors/translated-editors.utils';
import {
    removeTranslatedEditor,
    storeTranslatedEditors,
} from 'js/src/data/translated-editors/translated-editors-actions';
import { getSceneGroupIdsWithTranslation } from 'js/src/data/translated-editors/translated-editors-selectors';

export const hydrateTranslatedTranscriptsFromRedux = () => {
    return (dispatch: Dispatch, getState: GetRootState) => {
        const state = getState();
        const transcriptIds = getTranscriptIds(state);
        let serializedTranscripts: Record<string, SerializedTranscript> = {};
        serializedTranscripts = transcriptIds.reduce((transcriptMap, transcriptId) => {
            const immutableTranscript = getTranscriptById(state, transcriptId);
            if (!immutableTranscript)
                throw new Error(`Transcript with id ${transcriptId} not found`);
            const transcriptData = immutableTranscript.toJS();
            const { editorState, translations, disableOverlays } = transcriptData;
            const serializedTranscript = {
                editorState,
                translations,
                disableOverlays,
            };
            const sceneGroupId = immutableTranscript.get('sceneGroupId');
            transcriptMap[sceneGroupId] = serializedTranscript;
            return transcriptMap;
        }, serializedTranscripts);

        const translationEditors = reduceTranscriptsToTranslationEditorsMap(serializedTranscripts);
        translationEditors.forEach((translationEditor) =>
            dispatch(storeTranslatedEditors(translationEditor)),
        );

        // lookup scene group ids that is no longer in the new dictionary, remove those editors
        const existingSceneGroupIdsWithTranslation = getSceneGroupIdsWithTranslation(state);
        const sceneGroupIdsToRemove = existingSceneGroupIdsWithTranslation.filter(
            (sceneGroupId: string) => !sceneGroupHasTranscriptTranslation(state, sceneGroupId),
        );
        sceneGroupIdsToRemove.forEach((sceneGroupId: string) =>
            dispatch(removeTranslatedEditor({ sceneGroupId })),
        );
    };
};
